import { useEffect, useState } from 'react';
import { PlusCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { Poll, CreatePollPayload } from '@flock-types/polls.types';

const DURATION_PRESETS = [
  { label: '1 Day', days: 1 },
  { label: '3 Days', days: 3 },
  { label: '1 Week', days: 7 },
  { label: '2 Weeks', days: 14 },
  { label: '1 Month', days: 30 },
  { label: 'No End', days: null },
] as const;

interface OptionInput {
  id: number;
  label: string;
}

let nextId = 1;

interface Props {
  createPoll: (payload: CreatePollPayload) => Promise<Poll>;
  onCreated: (pollId: string) => void;
  onClose: () => void;
}

/** New Poll form (port of Expo poll/create.tsx as an EventFormModal-style dialog). */
export default function PollCreateModal({ createPoll, onCreated, onClose }: Props) {
  const [question, setQuestion] = useState('');
  const [description, setDescription] = useState('');
  const [options, setOptions] = useState<OptionInput[]>([
    { id: nextId++, label: '' },
    { id: nextId++, label: '' },
  ]);
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [showResultsBeforeClose, setShowResultsBeforeClose] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState<number | null>(7); // default 1 week
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filledOptions = options.filter((o) => o.label.trim().length > 0);
  const canCreate = question.trim().length > 0 && filledOptions.length >= 2 && !busy;

  const updateOption = (id: number, label: string) =>
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, label } : o)));

  const addOption = () => {
    if (options.length >= 20) return;
    setOptions((prev) => [...prev, { id: nextId++, label: '' }]);
  };

  const removeOption = (id: number) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((o) => o.id !== id));
  };

  const handleCreate = async () => {
    if (!canCreate) return;
    setBusy(true);
    setError(null);
    try {
      let closesAt: string | null = null;
      if (selectedDuration !== null) {
        const date = new Date();
        date.setDate(date.getDate() + selectedDuration);
        closesAt = date.toISOString();
      }
      const poll = await createPoll({
        question: question.trim(),
        description: description.trim() || undefined,
        options: filledOptions.map((o) => ({ label: o.label.trim() })),
        allowMultipleChoices: allowMultiple,
        isAnonymous,
        showResultsBeforeClose,
        closesAt,
      });
      onCreated(poll.id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create poll. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-flock-navy-900">New Poll</h2>
          <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">Question *</label>
            <textarea
              autoFocus
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="What would you like to ask?"
              className={inputClass}
            />
            <div className="mt-0.5 text-right text-xs text-flock-navy-400">
              {question.length}/500
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder="Add context or details for the group"
              className={inputClass}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">Options *</label>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={option.id} className="flex items-center gap-2">
                  <input
                    value={option.label}
                    onChange={(e) => updateOption(option.id, e.target.value)}
                    maxLength={255}
                    placeholder={`Option ${index + 1}`}
                    className={inputClass}
                  />
                  {options.length > 2 && (
                    <button
                      onClick={() => removeOption(option.id)}
                      title={`Remove option ${index + 1}`}
                      className="shrink-0 p-1 text-red-400 transition hover:text-red-600"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {options.length < 20 && (
              <button
                onClick={addOption}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-flock-gray-300 py-2.5 text-sm font-medium text-flock-navy-600 transition hover:border-flock-navy-400 hover:bg-flock-navy-50"
              >
                <PlusCircleIcon className="h-5 w-5" />
                Add Option
              </button>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">Settings</label>
            <div className="space-y-2">
              <ToggleRow
                label="Allow multiple choices"
                value={allowMultiple}
                onToggle={() => setAllowMultiple((v) => !v)}
              />
              <ToggleRow
                label="Anonymous voting"
                value={isAnonymous}
                onToggle={() => setIsAnonymous((v) => !v)}
              />
              <ToggleRow
                label="Show results before close"
                value={showResultsBeforeClose}
                onToggle={() => setShowResultsBeforeClose((v) => !v)}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-flock-navy-600">Duration</label>
            <div className="flex flex-wrap gap-2">
              {DURATION_PRESETS.map((preset) => {
                const isSelected = selectedDuration === preset.days;
                return (
                  <button
                    key={preset.label}
                    onClick={() => setSelectedDuration(preset.days)}
                    className={clsx(
                      'min-h-touch rounded-lg px-4 py-2 text-sm font-medium transition',
                      isSelected
                        ? 'bg-flock-navy-600 text-white'
                        : 'border border-flock-gray-300 bg-flock-gray-100 text-flock-navy-700 hover:border-flock-navy-400'
                    )}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <button
          onClick={handleCreate}
          disabled={!canCreate}
          className="min-h-touch mt-5 w-full rounded-lg bg-flock-navy-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-flock-navy-700 disabled:opacity-50"
        >
          {busy ? 'Creating…' : 'Create Poll'}
        </button>
      </div>
    </div>
  );
}

/** Settings toggle row (Expo ToggleRow port). */
function ToggleRow({
  label,
  value,
  onToggle,
}: {
  label: string;
  value: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="flex min-h-touch w-full items-center justify-between rounded-lg border border-flock-gray-200 bg-white px-3 py-2.5"
    >
      <span className="text-sm text-flock-navy-800">{label}</span>
      <span
        className={clsx(
          'flex h-6 w-10 items-center rounded-full px-0.5 transition',
          value ? 'justify-end bg-flock-navy-600' : 'justify-start bg-flock-gray-300'
        )}
      >
        <span className="h-5 w-5 rounded-full bg-white shadow-sm" />
      </span>
    </button>
  );
}
